import { useMemo } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { HistoryIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FieldGroup } from '@/components/ui/field';
import { Textarea } from '@/components/ui/textarea';

import { ConsignmentHistoryModal } from '@/widgets/consignment-history-modal';
import { FilterSelect, FormInput, FormField } from '@/shared/ui';

import { ConsignmentFormValues, getRequestStatusOptions, getPriorityOptions } from '../model';

interface ConsignmentControlCardProps {
  form: UseFormReturn<ConsignmentFormValues>;
  isReadOnly?: boolean;
  consignmentId?: string;
}

export function ConsignmentControlCard({ form, isReadOnly, consignmentId }: ConsignmentControlCardProps) {
  const { t } = useTranslation();
  const {
    register,
    control,
    formState: { errors }
  } = form;

  const requestStatusOptions = useMemo(() => getRequestStatusOptions(t), [t]);
  const priorityOptions = useMemo(() => getPriorityOptions(t), [t]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-medium text-muted-foreground">{t('newcmr.control.box')}</CardTitle>
      </CardHeader>
      <CardContent>
        <FieldGroup>
          <FormField label={t('consignment.fields.status.label')} name="status" error={errors.status?.message}>
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <FilterSelect<ConsignmentFormValues['status']>
                  value={field.value}
                  onChange={field.onChange}
                  options={requestStatusOptions}
                  placeholder={t('validity.valueMissing.select')}
                  width="full"
                  disabled={isReadOnly || !consignmentId}
                />
              )}
            />
          </FormField>

          <FormField label={t('consignment.fields.priority.label')} name="priority" error={errors.priority?.message}>
            <Controller
              name="priority"
              control={control}
              defaultValue="normal"
              render={({ field }) => (
                <FilterSelect<ConsignmentFormValues['priority']>
                  value={field.value}
                  onChange={field.onChange}
                  options={priorityOptions}
                  placeholder={t('validity.valueMissing.select')}
                  width="full"
                  disabled={isReadOnly}
                />
              )}
            />
          </FormField>

          <div className="flex justify-end">
            {consignmentId && (
              <ConsignmentHistoryModal
                consignmentId={consignmentId}
                trigger={
                  <Button type="button" variant="outline" size="sm">
                    <HistoryIcon className="mr-2 h-4 w-4" />
                    {t('newcmr.showhistory.button')}
                  </Button>
                }
              />
            )}
          </div>

          <FormField label={t('consignment.fields.remarks.label')} name="remarks" error={errors.remarks?.message}>
            <Textarea
              id="remarks"
              {...register('remarks')}
              placeholder={t('consignment.fields.remarks.placeholder')}
              rows={4}
              disabled={isReadOnly}
            />
          </FormField>

          <FormInput
            label={t('consignment.fields.transportUnitQuantity.label')}
            id="transportUnitQuantity"
            type="number"
            registration={register('transportUnitQuantity', { valueAsNumber: true })}
            placeholder={t('consignment.fields.transportUnitQuantity.placeholder')}
            disabled={isReadOnly}
            error={errors.transportUnitQuantity?.message}
          />
        </FieldGroup>
      </CardContent>
    </Card>
  );
}
